import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Grid, 
  Card, 
  CardContent,
  CircularProgress,
  Divider
} from "@mui/material";
import axios from "axios";

// コンポーネント
import ClientAutocomplete from "../../../components/ClientAutocomplete";
import ProductSelector from "./ProductSelector";

// MUIアイコン
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

/**
 * 残量だけを更新するセクション
 * 🚀 既存の中間テーブルがあり、予測機能+タンク設置が有効な製品のみ対象
 */
const RemainingUpdateSection = ({
  products,
  // 供給登録画面と同期するための状態
  onClientProductSelected = null,
  clientProductFromParent = null,
  // 親コンポーネントからのリセット関数追加
  onReset = null
}) => {
  // 顧客と製品の選択状態（このコンポーネント内で完結する場合）
  const [clientProducts, setClientProducts] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [selectedClientProductId, setSelectedClientProductId] = useState("");
  const [selectedClientProduct, setSelectedClientProduct] = useState(null);
  const [productLoading, setProductLoading] = useState(false);
  
  // 残量更新に関する状態
  const [remainingData, setRemainingData] = useState({
    amount: "",
    date: new Date().toISOString().split("T")[0]
  });
  const [remainingError, setRemainingError] = useState("");
  const [loading, setLoading] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  
  // 親コンポーネントからclientProductが渡された場合は同期
  useEffect(() => {
    if (clientProductFromParent) {
      setSelectedClientProductId(clientProductFromParent.id || "");
      setSelectedClientProduct(clientProductFromParent);
      setSelectedProductId(clientProductFromParent.productId || "");
      
      // 顧客情報は別途取得（必要に応じて）
      if (clientProductFromParent.clientId && !selectedClient) {
        // 顧客詳細を取得する場合のロジックをここに追加
      }
    }
  }, [clientProductFromParent]);
  
  // 🚀 修正：既存の予測機能有効な顧客製品一覧の取得（新規作成なし）
  const fetchClientProducts = async (clientId) => {
    if (!clientId) {
      setClientProducts([]);
      return;
    }
    
    try {
      setProductLoading(true);
      const token = sessionStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      
      // 🚀 予測機能有効な既存製品のみを取得する専用エンドポイントを使用
      const response = await axios.get(`/api/client-products/prediction-enabled?clientId=${clientId}`, { headers });
      
      // 🚀 追加チェック：フロントエンドでも両方の機能が有効かチェック
      const validProducts = response.data.filter(cp => 
        cp.predictionEnabled === true && cp.tankInstalled === true
      );
      
      setClientProducts(validProducts);
      
      
    } catch (err) {

      setClientProducts([]);
    } finally {
      setProductLoading(false);
    }
  };
  
  // 顧客選択変更時
  const handleClientChange = (client) => {
    setSelectedClient(client);
    setSelectedProductId("");
    setSelectedClientProductId("");
    setSelectedClientProduct(null);
    
    if (client) {
      fetchClientProducts(client.id);
    }
  };
  
  // 🚀 修正：製品選択変更時の処理（既存製品のみ、新規作成なし）
  const handleProductChange = async (productId) => {
    if (!productId || !selectedClient) return;
    
    setSelectedProductId(productId);
    setProductLoading(true);
    
    try {
      // 🚀 修正：既存の顧客製品のみ確認（新規作成は行わない）
      const existingClientProduct = clientProducts.find(cp => cp.productId === parseInt(productId, 10));
      
      if (existingClientProduct) {
        // 🚀 追加チェック：既存製品でも機能状態を再確認
        if (!existingClientProduct.predictionEnabled || !existingClientProduct.tankInstalled) {
          alert("この製品は予測機能またはタンク設置が無効のため残量更新できません。");
          setSelectedProductId("");
          setSelectedClientProductId("");
          setSelectedClientProduct(null);
          return;
        }
        
        // 既に存在する場合はそのIDを使用
        setSelectedClientProductId(existingClientProduct.id);
        setSelectedClientProduct(existingClientProduct);
        
        // 親コンポーネントに通知（必要な場合）
        if (onClientProductSelected) {
          onClientProductSelected(existingClientProduct);
        }
      } else {
        // 🚀 修正：残量更新では新規作成を行わない
        alert("残量更新は既存の取引関係がある製品でのみ可能です。");
        setSelectedProductId("");
        setSelectedClientProductId("");
        setSelectedClientProduct(null);
      }
    } catch (err) {
      alert("製品選択中にエラーが発生しました。");
      setSelectedClientProductId("");
      setSelectedClientProduct(null);
    } finally {
      setProductLoading(false);
    }
  };
  
  // 残量入力時の処理
  const handleRemainingChange = (value) => {
    // 数値のみ許可（空文字も可）
    if (value === "" || /^\d+$/.test(value)) {
      setRemainingData(prev => ({ ...prev, amount: value }));
      
      const num = parseInt(value, 10);
      if (value === "" || isNaN(num) || num < 0) {
        setRemainingError("0以上の値を入力してください");
      } else if (selectedClientProduct?.totalCapacity && num > selectedClientProduct.totalCapacity) {
        setRemainingError(`タンク容量（${selectedClientProduct.totalCapacity}L）以下で入力してください`);
      } else {
        setRemainingError("");
      }
    }
  };
  
  // 日付変更時の処理
  const handleDateChange = (value) => {
    const chosen = value;
    const today = new Date().toISOString().split("T")[0];
    if (chosen > today) {
      alert("未来の日付は登録できません。");
      setRemainingData(prev => ({ ...prev, date: today }));
    } else {
      setRemainingData(prev => ({ ...prev, date: chosen }));
    }
  };
  
  // 🚀 修正：残量更新処理の事前チェック強化
  const handleRemainingUpdate = async () => {
    if (!selectedClientProductId) {
      alert("顧客と製品を選択してください。");
      return;
    }
    
    // 🚀 追加チェック：選択した製品の機能状態を再確認
    if (!selectedClientProduct?.predictionEnabled || !selectedClientProduct?.tankInstalled) {
      alert("この製品は予測機能またはタンク設置が無効のため残量更新できません。");
      return;
    }
    
    if (!remainingData.amount || !remainingData.date) {
      alert("実測残量と測定日を両方入力してください。");
      return;
    }
    
    const remainingAmount = parseInt(remainingData.amount, 10);
    if (isNaN(remainingAmount) || remainingAmount < 0) {
      alert("残量は0以上の値で入力してください。");
      return;
    }
    
    // タンク容量チェック
    if (selectedClientProduct?.totalCapacity && remainingAmount > selectedClientProduct.totalCapacity) {
      alert(`残量はタンク容量（${selectedClientProduct.totalCapacity}L）以下で入力してください。`);
      return;
    }
    
    setLoading(true);
    
    const token = sessionStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      // 残量のみ更新APIを呼び出し
      await axios.put(
        `/api/client-products/${selectedClientProductId}/remaining`,
        {
          actualRemaining: parseInt(remainingData.amount, 10),
          measuredDate: remainingData.date
        },
        { headers }
      );
      
      // 成功メッセージを表示
      setUpdateSuccess(true);
      setTimeout(() => setUpdateSuccess(false), 3000);
      
      alert("残量を更新しました。");
      
      // 残量更新完了後、完全リセット
      if (onReset) {
        onReset(); // 親のresetSelections()を呼び出し
      } else {
        resetForm(); // フォールバック
      }
      
    } catch (err) {
      // 🚀 エラーハンドリング強化：具体的なエラーメッセージ表示
      let message = "残量更新に失敗しました。";
      if (err.response && typeof err.response.data === "string") {
        if (err.response.data.includes("予測機能が無効")) {
          message = "この製品は予測機能が無効のため残量更新できません。製品設定を確認してください。";
        } else if (err.response.data.includes("タンク設置されていない")) {
          message = "この製品はタンク設置されていないため残量更新できません。製品設定を確認してください。";
        } else {
          message = err.response.data;
        }
      }
      alert(message);
    } finally {
      setLoading(false);
    }
  };
  
  // 入力状態リセット
  const resetForm = () => {
    setRemainingData({
      amount: "",
      date: new Date().toISOString().split("T")[0]
    });
    setRemainingError("");
    setUpdateSuccess(false);
    
    // 親コンポーネントとの連携がない場合は、選択も初期化
    if (!clientProductFromParent) {
      setSelectedClient(null);
      setSelectedProductId("");
      setSelectedClientProductId("");
      setSelectedClientProduct(null);
      setClientProducts([]);
    }
  };
  
  return (
    <Card elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', mb: 4 }}>
      <Box sx={{ 
        bgcolor: 'info.main', 
        color: 'white',
        py: 2, 
        px: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <WaterDropIcon />
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          残量だけを更新
        </Typography>
      </Box>
      
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="body2" color="text.secondary" paragraph>
          供給せずに残量だけを記録する場合はこちらから更新できます。点検時や修正が必要な場合に使用してください。
          <strong>※ 既存の取引関係があり、予測機能とタンク設置が有効な製品のみ選択できます。</strong>
        </Typography>
        
        {/* 顧客と製品の選択(親コンポーネントから渡されない場合のみ表示) */}
        {!clientProductFromParent && (
          <>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                mb: 2, 
                fontWeight: 500,
                color: 'info.main',
              }}
            >
              顧客・製品の選択
            </Typography>
            
            {/* 顧客選択をClientAutocompleteに変更 */}
            <ClientAutocomplete
              selectedClient={selectedClient}
              onClientChange={handleClientChange}
              label="顧客"
              placeholder="顧客名で検索..."
              sx={{ mb: 2 }}
            />
            
            {/* 🚀 製品選択：既存の予測機能+タンク設置有効な製品のみ表示 */}
            <ProductSelector
              clientId={selectedClient?.id}
              products={products}
              selectedProductId={selectedProductId}
              onProductChange={handleProductChange}
              onClientProductChange={(cpId) => {
                setSelectedClientProductId(cpId);
                const cp = clientProducts.find(cp => cp.id === cpId);
                if (cp) setSelectedClientProduct(cp);
              }}
              predictionEnabledOnly={true} // 🚀 既存の機能有効製品のみ表示
            />
            
            <Divider sx={{ my: 3 }} />
          </>
        )}
        
        {/* 残量更新フォーム */}
        <Typography 
          variant="subtitle2" 
          sx={{ 
            mb: 2, 
            fontWeight: 500,
            color: 'info.main',
          }}
        >
          残量情報
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="実測残量 (L)"
              fullWidth
              value={remainingData.amount}
              onChange={(e) => {
                const value = e.target.value;
                // 数値のみ許可（空文字も可）
                if (value === "" || /^\d+$/.test(value)) {
                  handleRemainingChange(value);
                }
              }}
              error={Boolean(remainingError)}
              helperText={remainingError || (selectedClientProduct?.totalCapacity ? `タンク容量: ${selectedClientProduct.totalCapacity}L` : "")}
              InputProps={{
                startAdornment: (
                  <WaterDropIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                ),
                inputProps: {
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  min: "0",
                  max: selectedClientProduct?.totalCapacity || "999999"
                }
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              label="実測日"
              type="date"
              fullWidth
              value={remainingData.date}
              onChange={(e) => handleDateChange(e.target.value)}
              InputProps={{
                inputProps: {
                  max: new Date().toISOString().split("T")[0]
                },
                startAdornment: (
                  <CalendarTodayIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                )
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
          {/* 成功メッセージ表示 */}
          {updateSuccess && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleOutlineIcon fontSize="small" color="success" />
              <Typography variant="body2" color="success.main" sx={{ whiteSpace: 'nowrap' }}>
                残量を更新しました
              </Typography>
            </Box>
          )}
          
          {/* ボタンエリア */}
          <Box sx={{ display: 'flex', gap: 2, ml: 'auto' }}>
            <Button
              variant="outlined"
              color="info"
              onClick={onReset || resetForm}
              disabled={loading}
            >
              リセット
            </Button>
            <Button
              variant="contained"
              color="info"
              onClick={handleRemainingUpdate}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircleOutlineIcon />}
              disabled={!selectedClientProductId || !remainingData.amount || Boolean(remainingError) || loading}
            >
              残量を更新
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default RemainingUpdateSection;